import {
  Avatar,
  Button,
  Grid,
  Link,
  makeStyles,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import { useForm } from 'react-hook-form'
import { ForgotInfo, PasswordInfo, useChangePassword, useForgotPassword } from '../api'
import React, { useState } from 'react'
import { LockIcon } from '../icons'
import { useCaptcha } from '../Captcha'
import { Link as RouterLink } from 'react-router-dom'
import { strings } from '../strings'
import { PasswordField } from '../PasswordField'
import { LoadingButton } from '../LoadingButton'

export function ForgotPassword() {
  const classes = useStyles()
  const [step, setStep] = useState(1)
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')

  let content
  switch (step) {
    case 1:
      content = (
        <StepOne
          onNextStep={() => {
            setStep(2)
            setError('')
          }}
          setUsername={setUsername}
        />
      )
      break
    case 2:
      content = (
        <StepTwo
          onPrevStep={(err: any) => {
            setStep(1)
            setError(err)
          }}
          onNextStep={() => setStep(3)}
          username={username}
        />
      )
      break
    case 3:
      content = <StepThree />
      break
  }

  return (
    <div className={classes.paper}>
      {error && (
        <>
          <Typography color="error">{strings.forgotPasswordChangeError}</Typography>
          <Typography color="error">{error}</Typography>
        </>
      )}
      <Avatar className={classes.avatar}>
        <LockIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        فراموشی رمز
      </Typography>
      {content}
      <Grid container>
        <Grid item xs>
          <Link component={RouterLink} to="/auth/signup">
            حساب ندارید؟ ثبت نام
          </Link>
        </Grid>
        <Grid item>
          <Link component={RouterLink} to="/auth/signin">
            حساب دارید؟ ورود
          </Link>
        </Grid>
      </Grid>
    </div>
  )
}

function StepOne({ onNextStep: handleNextStep, setUsername }: any) {
  const classes = useStyles()
  const [captcha, refreshCaptcha, getCaptchaInfo] = useCaptcha()
  const sendOtp = useForgotPassword()
  const { register, handleSubmit, errors } = useForm<ForgotInfo>()
  const [[inProgress, error], setState] = useState([false, ''])

  const onSubmit = (formData: ForgotInfo) => {
    setState([true, ''])
    const captchaInfo = getCaptchaInfo()

    setUsername(formData.username)

    sendOtp(formData, captchaInfo)
      .then(() => {
        setState([false, ''])
        handleNextStep()
      })
      .catch((err) => {
        setState([false, err.message])
        refreshCaptcha()
      })
  }

  return (
    <form noValidate className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <CustomTextField
        name="username"
        label={strings.usernameOrNatinalCode}
        inputRef={register({ required: true, minLength: 4 })}
        error={!!errors.username}
        autoFocus
      />
      <CustomTextField
        name="phoneNumber"
        label={strings.phoneNumber}
        inputRef={register({ required: true, pattern: /^09\d{9}$/ })}
        error={!!errors.phoneNumber}
      />
      {captcha}
      <Typography color="error">{error}</Typography>
      <LoadingButton
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        text={strings.sendOtp}
        disabled={inProgress}
        loading={inProgress}
      />
    </form>
  )
}

function StepTwo({ onPrevStep: handlePrevStep, onNextStep: handleNextStep, username }: any) {
  const classes = useStyles()
  const [captcha, refreshCaptcha, getCaptchaInfo] = useCaptcha()
  const changePasswordWithOtp = useChangePassword()
  const { register, handleSubmit, errors } = useForm<PasswordInfo>()
  const [[inProgress, error], setState] = useState([false, ''])

  const onSubmit = (formData: PasswordInfo) => {
    setState([true, ''])
    const captchaInfo = getCaptchaInfo()

    changePasswordWithOtp({ ...formData, username }, captchaInfo)
      .then(() => {
        setState([false, ''])
        handleNextStep()
      })
      .catch((err) => {
        handlePrevStep(err.message)
      })
  }

  return (
    <form noValidate className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <CustomTextField
        name="otpCode"
        label={strings.otpCode}
        inputRef={register({ required: true })}
        error={!!errors.otpCode}
        autoFocus
      />
      <PasswordField
        name="newPassword"
        label={strings.newPassword}
        inputRef={register({
          required: true,
          minLength: { value: 8, message: strings.passwordMinLengthHelper },
          pattern: {
            value:
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{};':"\\|,.<>\/?`~\-]).{8,}$/,
            message: strings.passwordFormatHelper,
          },
        })}
        error={!!errors.newPassword}
        helperText={errors.newPassword?.message}
        margin="normal"
        autoComplete="off"
        variant="outlined"
        fullWidth
      />
      {captcha}
      <Typography color="error">{error}</Typography>
      <Grid container>
        <Grid item xs>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            className={classes.submit}
            fullWidth
            text={strings.apply}
            disabled={inProgress}
            loading={inProgress}
          />
        </Grid>
        <Grid item style={{ width: '16px' }} />
        <Grid item xs style={{ textAlign: 'left' }}>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            className={classes.submit}
            fullWidth
            onClick={handlePrevStep}
          >
            {strings.cancel}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

function StepThree() {
  return <h1>Done</h1>
}

function CustomTextField(props: TextFieldProps) {
  return <TextField margin="normal" autoComplete="off" variant="outlined" fullWidth {...props} />
}

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}))
